import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Paperclip, StickyNote, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import HorsePickerInline from './HorsePickerInline';
import AttachmentPickerSheet from '../shared/AttachmentPickerSheet';
import LinkedNoteDialog from './LinkedNoteDialog';
import MultiDatePicker from './MultiDatePicker';
import { useConfirm } from '../../components/common/ConfirmDialog';

function localNow() {
  const now = new Date();
  return {
    date: now.toLocaleDateString('sv'),
    time: now.toTimeString().slice(0, 5),
  };
}

export default function QuickLogModal({ open, onClose, onSaved, horse, locations, catalogItems, prefill, scheduledItem, activeLayup, availableHorses, onKeepInCare }) {
  const { date: todayDate, time: nowTime } = localNow();
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);
  const confirm = useConfirm();

  // Dynamic selection state (used when no prefill category — "Other" button)
  const [dynCategory, setDynCategory] = useState('');
  const [dynType, setDynType]         = useState('');

  const effectiveCategory = scheduledItem?.category || prefill?.category || dynCategory;
  const effectiveType     = scheduledItem?.type     || prefill?.type     || dynType;
  const isDynamic         = !scheduledItem && !prefill?.category;
  const isComplete        = !!scheduledItem;

  // Derive available categories/types from catalog
  const catalogCategories = [...new Set((catalogItems || []).map(c => c.category))].filter(Boolean).sort();
  const catalogTypes      = [...new Set((catalogItems || []).filter(c => c.category === effectiveCategory).map(c => c.type))].filter(Boolean).sort();

  // Catalog items filtered to effective category + type
  const filtered = (catalogItems || []).filter(c =>
    c.category === effectiveCategory &&
    (!effectiveType || c.type === effectiveType)
  );

  const initForm = () => {
    const { date: currentDate, time: currentTime } = localNow();
    return {
    care_catalog_item_id: scheduledItem?.care_catalog_item_id || null,
    name: scheduledItem?.name || '',
    dosage: scheduledItem?.dosage || '',
    dosage_unit: scheduledItem?.dosage_unit || '',
    units: scheduledItem?.units ?? '',
    log_date: currentDate,
    log_time: currentTime,
    location_id: scheduledItem?.location_id || '',
    performed_by: '',
    performed_by_type: 'In-house',
    comments: '',
    publish_to_owner_portal: false,
    billable: scheduledItem?.billable || false,
    cost: scheduledItem?.cost ?? '',
    cost_currency: scheduledItem?.cost_currency || 'USD',
    };
  };

  const [form, setForm]               = useState(initForm);
  const [dates, setDates]             = useState([]);
  const [saving, setSaving]           = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading]     = useState(false);
  const [showNote, setShowNote]       = useState(false);
  const [selectedHorseIds, setSelectedHorseIds] = useState([]);
  const [horseSearch, setHorseSearch] = useState('');
  const isMulti = Array.isArray(availableHorses);

  useEffect(() => {
    if (open) {
      const { date: todayDate, time: nowTime } = localNow();
      setForm(initForm());
      setDates([{ id: Math.random().toString(36).slice(2), date: todayDate, time: nowTime }]);
      setAttachments([]);
      setShowNote(false);
      setDynCategory('');
      setDynType('');
      setSelectedHorseIds([]);
      setHorseSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill, scheduledItem]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleCatalogSelect = (itemId) => {
    if (itemId === '__none__') { f('care_catalog_item_id', null); f('name', ''); f('dosage', ''); f('dosage_unit', ''); f('billable', false); f('cost', ''); return; }
    const item = filtered.find(c => c.id === itemId);
    if (item) {
      setForm(p => ({
        ...p,
        care_catalog_item_id: item.id,
        name: item.name,
        dosage: item.dosage || '',
        dosage_unit: item.dosage_unit || '',
        billable: item.billable || false,
        cost: item.cost ?? '',
        cost_currency: item.cost_currency || p.cost_currency || 'USD',
      }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files.map(file => ({ name: file.name, file }))]);
    e.target.value = '';
  };

  const handleSave = async () => {
    const entryName = form.name || scheduledItem?.name || '';
    if (!entryName.trim()) { toast.error('Please select a name from the catalog'); return; }
    if (isMulti && selectedHorseIds.length === 0) { toast.error('Select at least one horse'); return; }
    if (!dates.length) { toast.error('Please select at least one date'); return; }
    if (effectiveCategory === 'Conditioning' && activeLayup?.restrictions?.pause_conditioning) {
      if (!await confirm({ title: `⚠️ ${horse?.name || 'This horse'} is on LAYUP with conditioning paused. Log this anyway?`, destructive: true })) return;
    }
    setSaving(true);
    try {
      const basePayload = {
        horse_id: horse?.id,
        care_catalog_item_id: form.care_catalog_item_id,
        category: effectiveCategory,
        type: effectiveType,
        name: entryName,
        dosage: form.dosage,
        dosage_unit: form.dosage_unit,
        units: form.units !== '' ? parseFloat(form.units) || null : null,
        location_id: form.location_id || null,
        location_name: locations?.find(l => l.id === form.location_id)?.name || '',
        performed_by: form.performed_by,
        performed_by_type: form.performed_by_type,
        comments: form.comments,
        publish_to_owner_portal: form.publish_to_owner_portal,
        billable: form.billable || false,
        cost: form.billable && form.cost !== '' ? parseFloat(form.cost) || null : null,
        cost_currency: form.cost_currency || 'USD',
      };

      let lastEntry = null;

      if (isComplete) {
        // Completing a scheduled item — always single date
        const res = await api.post(`/farmfolio/scheduled-care/${scheduledItem.id}/complete`, {
          ...basePayload, log_date: dates[0].date, log_time: dates[0].time || '',
        });
        lastEntry = res?.data;
      } else {
        // One entry per selected date
        for (const dateEntry of dates) {
          const payload = { ...basePayload, log_date: dateEntry.date, log_time: dateEntry.time || '' };
          let res;
          if (isMulti) {
            res = await api.post('/farmfolio/global/bulk/care-log', { horse_ids: selectedHorseIds, payload });
          } else {
            res = await api.post('/farmfolio/care-log', payload);
          }
          if (res?.data) lastEntry = res.data;
        }
      }

      if (attachments.length > 0) {
        setUploading(true);
        await Promise.allSettled(
          attachments.map(({ file }) => {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('horse_id', horse.id);
            fd.append('description', `${prefill?.type || effectiveType || 'Care'} — ${dates[0].date}`);
            return api.post('/files/upload', fd);
          })
        );
        setUploading(false);
      }

      const count = isComplete ? 1 : dates.length;
      toast.success(isComplete ? 'Care completed' : `Care logged${count > 1 ? ` (${count} entries)` : ''}`);
      onSaved(lastEntry);
      onClose();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const careRef = `${prefill?.type || 'Care'} — ${dates[0]?.date || ''}${dates[0]?.time ? ' @ ' + dates[0].time : ''}`;
  const selectedItem = filtered.find(c => c.id === form.care_catalog_item_id);
  const dosageDisplay = selectedItem
    ? (selectedItem.dosage
        ? `${selectedItem.dosage}${selectedItem.dosage_unit ? ' ' + selectedItem.dosage_unit : ''}`.trim()
        : selectedItem.serving_size != null
          ? `${selectedItem.serving_size}${selectedItem.serving_size_units ? ' ' + selectedItem.serving_size_units : ''}`.trim()
          : '')
    : form.dosage ? `${form.dosage}${form.dosage_unit ? ' ' + form.dosage_unit : ''}`.trim() : '';

  // Show Quantity field for Medical, Daily Care, and Conditioning
  const showQuantity = ['Medical', 'Daily Care', 'Conditioning'].includes(effectiveCategory);

  const isSoldNoCare = horse?.sale_status === 'Sold' && !horse?.keep_in_care;
  // For multi-select mode: derive sold state from the first selected horse
  const selectedSoldHorse = isMulti && selectedHorseIds.length === 1
    ? (availableHorses || []).find(h => h.id === selectedHorseIds[0] && h.sale_status === 'Sold' && !h.keep_in_care)
    : null;
  const activeSoldWarning = isSoldNoCare ? horse : selectedSoldHorse;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isComplete ? 'Complete Care' : 'Quick Log Care'}</DialogTitle>
          </DialogHeader>

          {/* Sold warning banner — shown when horse is sold without keep_in_care */}
          {activeSoldWarning && (
            <div className="flex items-start justify-between gap-2 p-2.5 rounded bg-gray-100 border border-gray-300 text-sm">
              <span className="text-gray-700">
                {activeSoldWarning.sold_date
                  ? `Sold on ${new Date(activeSoldWarning.sold_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}. Automated care is paused.`
                  : 'Horse has been sold. Automated care is paused.'}
              </span>
              {onKeepInCare && (
                <button
                  className="shrink-0 text-xs font-medium text-primary hover:underline whitespace-nowrap"
                  onClick={() => onKeepInCare(activeSoldWarning.id)}
                >
                  Keep in Care
                </button>
              )}
            </div>
          )}

          <div className="space-y-3 py-2">
            {isMulti && (
              <HorsePickerInline availableHorses={availableHorses}
                value={selectedHorseIds} onChange={setSelectedHorseIds} mode="multi" locations={locations} />
            )}

            {/* Category — read-only when from button, dropdown when "Other" */}
            {isDynamic ? (
              <div className="space-y-1">
                <Label className="font-semibold">Category</Label>
                <Select value={dynCategory || '__none__'} onValueChange={v => {
                  const val = v === '__none__' ? '' : v;
                  setDynCategory(val); setDynType('');
                  setForm(p => ({ ...p, care_catalog_item_id: null, name: '' }));
                }}>
                  <SelectTrigger><SelectValue placeholder="Select category…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Select —</SelectItem>
                    {catalogCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="rounded border px-3 py-2 text-sm bg-muted/20">
                <span className="font-semibold">Category:</span> {effectiveCategory}
              </div>
            )}

            {/* Type — read-only when from button, dropdown when "Other" */}
            {isDynamic ? (
              <div className="space-y-1">
                <Label className="font-semibold">Type</Label>
                <Select value={dynType || '__none__'} onValueChange={v => {
                  const val = v === '__none__' ? '' : v;
                  setDynType(val);
                  setForm(p => ({ ...p, care_catalog_item_id: null, name: '' }));
                }} disabled={!dynCategory}>
                  <SelectTrigger><SelectValue placeholder={dynCategory ? 'Select type…' : 'Select category first'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Select —</SelectItem>
                    {catalogTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="rounded border px-3 py-2 text-sm bg-muted/20">
                <span className="font-semibold">Type:</span> {effectiveType}
              </div>
            )}

            {/* Name — read-only when completing, catalog dropdown otherwise */}
            {isComplete ? (
              <div className="rounded border px-3 py-2 text-sm bg-muted/20">
                <span className="font-semibold">Name:</span> {scheduledItem.name}
                {scheduledItem.dosage && ` (${scheduledItem.dosage}${scheduledItem.dosage_unit ? ' ' + scheduledItem.dosage_unit : ''})`}
              </div>
            ) : (
              <div className="space-y-1">
                <Label className="font-semibold">Name</Label>
                <Select value={form.care_catalog_item_id || '__none__'} onValueChange={handleCatalogSelect}>
                  <SelectTrigger><SelectValue placeholder={filtered.length ? 'Select from catalog…' : 'No catalog items'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Select —</SelectItem>
                    {filtered.map(item => {
                      const detail = item.dosage
                        ? `${item.dosage}${item.dosage_unit ? ' ' + item.dosage_unit : ''}`
                        : item.serving_size != null
                          ? `${item.serving_size}${item.serving_size_units ? ' ' + item.serving_size_units : ''}`
                          : null;
                      return (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name}{detail ? ` (${detail})` : ""}{ (item.is_billable ?? item.billable) ? " (billable)" : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Quantity — decimal input */}
            {showQuantity && (
              <div className="space-y-1">
                <Label className="font-semibold">Quantity</Label>
                <Input type="number" min="0" step="0.01" value={form.units}
                  onChange={e => f('units', e.target.value)} placeholder="e.g. 1, 2.5" />
              </div>
            )}

            {/* Date / Time — single or multi */}
            <div className="space-y-1">
              <Label className="font-semibold">Date & Time</Label>
              <MultiDatePicker
                value={dates}
                onChange={setDates}
                defaultTime={scheduledItem?.preferred_time || localNow().time}
              />
            </div>

            {/* Location */}
            <div className="space-y-1">
              <Label className="font-semibold">Location</Label>
              <Select value={form.location_id || '__none__'} onValueChange={v => f('location_id', v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {(locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* By — In-house / Vet toggle */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">By</Label>
                <div className="flex items-center gap-2 text-sm">
                  <span className={form.performed_by_type === 'In-house' ? 'text-primary font-medium' : 'text-muted-foreground'}>In-house</span>
                  <Switch checked={form.performed_by_type !== 'In-house'}
                    onCheckedChange={v => f('performed_by_type', v ? 'Other' : 'In-house')} />
                  <span className={form.performed_by_type !== 'In-house' ? 'text-primary font-medium' : 'text-muted-foreground'}>Other</span>
                </div>
              </div>
              <Input value={form.performed_by} onChange={e => f('performed_by', e.target.value)}
                placeholder={form.performed_by_type !== 'In-house' ? "Person's name" : "Person's name"} />
            </div>

            {/* Comments */}
            <div className="space-y-1">
              <Label className="font-semibold">Comments</Label>
              <Textarea rows={3} value={form.comments} onChange={e => f('comments', e.target.value)} />
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <Button type="button" variant="outline" className="w-full justify-start gap-2"
                onClick={() => setAttachPickerOpen(true)}>
                <Paperclip className="w-4 h-4" /> Upload Attachments
              </Button>
              {attachments.length > 0 && (
                <div className="space-y-1">
                  {attachments.map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-muted-foreground bg-muted/30 rounded px-2 py-1">
                      <span className="truncate">{a.name}</span>
                      <Button variant="ghost" size="icon" className="h-4 w-4 shrink-0"
                        onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Linked Note */}
            <div>
              <Button type="button" variant="outline" className="w-full justify-start gap-2"
                onClick={() => setShowNote(true)}>
                <StickyNote className="w-4 h-4" /> Create Linked Note
              </Button>
            </div>

            {/* Publish to Owner Portal */}
            <div className="flex items-center gap-2">
              <Switch checked={form.publish_to_owner_portal} onCheckedChange={v => f('publish_to_owner_portal', v)} />
              <Label className="cursor-pointer text-sm">Publish to Owner Portal</Label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t mt-2">
            <Button variant="outline" onClick={onClose} disabled={saving || uploading}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || uploading}
              className="bg-primary hover:bg-primary/90 text-white">
              {saving || uploading ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <LinkedNoteDialog
        open={showNote}
        onClose={() => setShowNote(false)}
        onSaved={() => setShowNote(false)}
        horse={horse}
        careEntryRef={careRef}
      />
      <AttachmentPickerSheet
        open={attachPickerOpen}
        onClose={() => setAttachPickerOpen(false)}
        onFiles={(files) => setAttachments(prev => [...prev, ...files.map(f => ({ name: f.name, file: f }))])}
      />
    </>
  );
}
